import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DocumentScanner, VideoCameraBackOutlined } from '@mui/icons-material';
import { MediaFile } from 'types/files';
import useLocalization from 'hooks/useLocalization';

interface FilesGalleryViewProps {
  files: MediaFile[];
  type: string;
  handleDeleteFileFromGallery: (file: MediaFile) => void;
  onEditExistingNameClick: (file: MediaFile) => void;
  handleLoadFilesListOnScroll: (e: any, offset: number) => void;
  noActions?: boolean;
  onClickAction: (file: any) => void;
}

const FilesGalleryView: React.FC<FilesGalleryViewProps> = ({
  files,
  type,
  handleDeleteFileFromGallery,
  onEditExistingNameClick,
  handleLoadFilesListOnScroll,
  noActions,
  onClickAction,
}) => {
  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(() => currentLocale.settingsPopup.uploadFiles, [currentLocale]);

  const handleContextMenu = (event: React.MouseEvent<HTMLButtonElement>, file: MediaFile) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setSelectedFile(null);
    }, 300);
  };

  return (
    <Box
      maxHeight={'100%'}
      overflow={'auto'}
      px={1}
      py={1.5}
      onScroll={(e) => handleLoadFilesListOnScroll(e, 200)}
    >
      <Grid container spacing={3} sx={{ padding: '8px' }}>
        {files.map((file) => (
          <Grid 
            key={file.Id} 
            size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2.4 }} 
            sx={{
              minWidth: 220,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Card
              sx={{
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                overflow: 'hidden',
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.08)',
                  borderColor: 'primary.main',
                  '& .file-overlay': {
                    opacity: 1,
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, transparent, primary.main, transparent)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 1,
                },
              }}
              onClick={() => onClickAction(file)}
            >
              <Box sx={{ position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                {type === 'image' ? (
                  <CardMedia
                    component={'img'}
                    height="100%"
                    image={file.Link}
                    alt={file.Name}
                    sx={{
                      aspectRatio: 2 / 1.25,
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                ) : (
                  <Box
                    height="100%"
                    sx={{
                      aspectRatio: 2 / 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.2), transparent)',
                      },
                    }}
                  >
                    {type === 'video' && (
                      <VideoCameraBackOutlined 
                        sx={{ 
                          fontSize: 60, 
                          color: 'white',
                          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                          zIndex: 1,
                        }} 
                      />
                    )}
                    {type === 'document' && (
                      <DocumentScanner 
                        sx={{ 
                          fontSize: 60, 
                          color: 'white',
                          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                          zIndex: 1,
                        }} 
                      />
                    )}
                  </Box>
                )}
                <Box
                  className="file-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  }}
                />
              </Box>

              <CardContent
                sx={{
                  padding: '16px 20px 20px',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(248,250,252,0.98))',
                  backdropFilter: 'blur(10px)',
                  borderTop: 'none',
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Grid container spacing={1} alignItems={'center'}>
                  <Grid size={12}>
                    <Tooltip title={file.Name} placement="bottom-start">
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: 'text.primary',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                          lineHeight: 1.4,
                          mb: 0.5,
                        }}
                      >
                        {file.Name}
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid size="grow">
                    <Typography 
                      variant="body2" 
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontWeight: 400,
                      }}
                    >
                      {file.DateCreate}
                    </Typography>
                  </Grid>
                  {!noActions && (
                    <Grid size="auto">
                      <IconButton
                        onClick={(event) => handleContextMenu(event, file)}
                        sx={{
                          padding: '6px',
                          borderRadius: '8px',
                          color: 'text.secondary',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Menu open={Boolean(anchorEl)} onClose={handleClose} anchorEl={anchorEl}>
        <MenuItem
          onClick={() => {
            if (selectedFile) handleDeleteFileFromGallery(selectedFile);
            handleClose();
          }}
        >
          {localization.menu.delete}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedFile) onEditExistingNameClick(selectedFile);
            handleClose();
          }}
        >
          {localization.menu.editName}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FilesGalleryView;
